import { useEffect, useReducer } from "react";

/**
 * Live viewport numbers, for diagnosing a full-screen surface on a real device.
 *
 * Every interesting difference between iOS Safari, Android Chrome and the
 * native WebView is invisible on a desktop browser — a laptop has one viewport,
 * one height and no keyboard, so an emulated phone reproduces the layout but
 * not the platform. This prints what the device actually reports, which turns
 * "the bottom still scrolls" into a number.
 *
 * Renders nothing unless the URL carries `?vpdebug=1`.
 */
export function ViewportDebug() {
  const [, sample] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", sample);
    viewport?.addEventListener("scroll", sample);
    window.addEventListener("resize", sample);
    window.addEventListener("scroll", sample, { passive: true });
    document.addEventListener("focusin", sample);
    document.addEventListener("focusout", sample);
    // The events above miss the keyboard's animation frames, and a stale
    // reading is exactly what would send us chasing the wrong number.
    const timer = setInterval(sample, 250);
    return () => {
      viewport?.removeEventListener("resize", sample);
      viewport?.removeEventListener("scroll", sample);
      window.removeEventListener("resize", sample);
      window.removeEventListener("scroll", sample);
      document.removeEventListener("focusin", sample);
      document.removeEventListener("focusout", sample);
      clearInterval(timer);
    };
  }, []);

  if (!new URLSearchParams(window.location.search).has("vpdebug")) return null;

  const root = document.documentElement;
  const viewport = window.visualViewport;
  const round = (n: number | undefined) => (n === undefined ? "-" : Math.round(n));
  const scroller = document.scrollingElement ?? root;
  const shell = document.querySelector(".h-app-viewport");

  const rows: [string, string | number][] = [
    ["window.innerHeight", window.innerHeight],
    ["visualViewport.h", round(viewport?.height)],
    ["visualViewport.top", round(viewport?.offsetTop)],
    ["--app-vh", root.style.getPropertyValue("--app-vh") || "(unset)"],
    ["--app-vv-top", root.style.getPropertyValue("--app-vv-top") || "(unset)"],
    ["html height", getComputedStyle(root).height],
    ["body position", getComputedStyle(document.body).position],
    ["body height", getComputedStyle(document.body).height],
    ["shell top", `${Math.round(shell?.getBoundingClientRect().top ?? NaN)}`],
    // `shell top` alone cannot tell "the transform is working" from "the
    // transform never applied": getBoundingClientRect's coordinate space
    // differs between browsers once the visual viewport is offset, and both
    // answers read as 0. The computed style is the same on every browser.
    ["shell position", shell ? getComputedStyle(shell).position : "-"],
    ["shell transform", shell ? getComputedStyle(shell).transform : "-"],
    ["locked", String(root.classList.contains("app-viewport-locked"))],
    ["data-keyboard", root.getAttribute("data-keyboard") ?? "(none)"],
    ["window.scrollY", Math.round(window.scrollY)],
    ["doc scrollTop", Math.round(scroller.scrollTop)],
    ["doc scrollHeight", scroller.scrollHeight],
    ["focused", document.activeElement?.tagName ?? "-"],
  ];

  return (
    <div
      className="fixed top-0 left-0 z-[10000] m-1 rounded bg-black/85 px-2 py-1 font-mono text-[10px] leading-[1.35] text-lime-300 pointer-events-none"
      aria-hidden="true"
    >
      {rows.map(([label, value]) => (
        <div key={label}>
          {label}: <span className="text-white">{value}</span>
        </div>
      ))}
    </div>
  );
}
